/**
 * Every number the matcher can be tuned by, in one object.
 *
 * Two reasons it is all here rather than scattered through the scorer. The obvious one is
 * that the eval harness sweeps these, and a weight buried in a function body cannot be swept.
 * The less obvious one is that it makes the layers *falsifiable*: `layers.romanization =
 * false` and re-run tells you what the romanisation table is worth, in points of top-1
 * accuracy, on real cases. A layer that cannot be switched off cannot be shown to be earning
 * its complexity, and BHCS-100 asks for exactly that evidence.
 */

/**
 * How sure each route alone is allowed to make us, 0–1.
 *
 * These started as BHCS-100's guesses, which were written as the weights of an average. The
 * harness showed within its first run that an average was the wrong combiner — see `combine`
 * in `voiceNameMatch.ts` — so they were re-read as caps, which is what they had always
 * really meant. A route at 0.15 cannot carry a child on its own however certain it is; a
 * route at 0.95 can. They do not sum to 1 and nothing normalises them.
 */
export interface MatchWeights {
  /** Confusion-weighted syllable similarity — the main event. */
  syllable: number;
  /** Initials agree: "lsy" for Li Si Yuan. Cheap, and strong on a badly mangled transcript. */
  initials: number;
  /** The tuned phonetic key, this codebase's stand-in for double metaphone. */
  phonetic: number;
  /**
   * The same route, capped lower, for the pairs where the syllable route also fired.
   *
   * The phonetic key is letters with the vowels dropped, so it is confidently wrong exactly
   * where the spelling diverges most: "shanghai" shares almost every letter with the surname
   * Shang and almost none with Xuankai, and scored 0.83 against the wrong child and 0.50
   * against the right one. The syllable route had it right at 0.78 and was overruled by a
   * letter coincidence holding an equal cap.
   *
   * When both names can be read as Mandarin, the syllable reading is the better evidence and
   * the letter reading is mostly redundant with it — which is what Peng, Yu & Dredze (ACL
   * 2015) report when they find segmented pinyin beats string matching on Chinese names. So
   * the letter route keeps its full authority only where the syllable route is silent: an
   * English given name, or a string no segmentation can read.
   */
  phoneticWithSyllable: number;
  /** An English or preferred given name, matched as an English word rather than as pinyin. */
  english: number;
  /**
   * This teacher picked this child recently.
   *
   * Read differently from the others: not a cap on a route, but the fraction of the score's
   * remaining headroom that a perfectly fresh pick may add. Small on purpose — a sibling
   * collected every afternoon must never shadow the child actually named.
   */
  recency: number;
}

/** Each layer, switchable, so the harness can attribute a gain to one of them. */
export interface MatchLayers {
  /** Split syllables into initial + final and score with the confusion matrix. */
  syllable: boolean;
  /** Rewrite English vowel spellings into pinyin before segmenting. */
  englishSpelling: boolean;
  /** Map Chan/Cheung/Teoh back onto the pinyin the roster spells. */
  romanization: boolean;
  /** Index every reading of a multi-pronunciation character. Dormant: no Chinese names on file. */
  heteronym: boolean;
  /** Re-glue words the recogniser chopped up ("zee you on" → "zeeyouon"). */
  regluing: boolean;
  /** Learned aliases from teachers' own corrections. */
  alias: boolean;
  /** The recency prior. */
  recency: boolean;
}

export interface MatchConfig {
  weights: MatchWeights;
  layers: MatchLayers;
  /** Below this a child is not offered at all. */
  floor: number;
  /** How far behind the leader a child may be and still be offered. */
  spread: number;
  /** The most children ever offered at once. */
  maxSuggestions: number;
  /**
   * How far ahead of #2 the leader must be before the UI may act without the teacher
   * choosing. Not used to auto-record anything today — a check-out hands a child to an
   * adult, and no confidence score should be allowed to do that unattended. It exists so the
   * harness can report how often a gap this size would have been right.
   */
  autoSelectGap: number;
  /** An exact hit on a learned alias, which should beat anything an algorithm produces. */
  aliasScore: number;
  /** Days after which the recency prior has fully decayed. */
  recencyHalfLifeDays: number;
  /**
   * How much agreement between routes may raise a score, 0–1, as a fraction of the gap
   * still left to 1. At 0 the best route decides alone.
   */
  corroboration: number;
}

export const DEFAULT_CONFIG: MatchConfig = {
  weights: {
    syllable: 0.95,
    phonetic: 0.95,
    phoneticWithSyllable: 0.82,
    initials: 0.55,
    english: 0.95,
    recency: 0.08,
  },
  layers: {
    syllable: true,
    englishSpelling: true,
    romanization: true,
    heteronym: true,
    regluing: true,
    alias: true,
    recency: true,
  },
  floor: 0.45,
  spread: 0.25,
  maxSuggestions: 5,
  autoSelectGap: 0.20,
  aliasScore: 0.99,
  recencyHalfLifeDays: 14,
  corroboration: 0.5,
};

/** A config with one layer switched off, for the harness's ablation table. */
export function withoutLayer(config: MatchConfig, layer: keyof MatchLayers): MatchConfig {
  return { ...config, layers: { ...config.layers, [layer]: false } };
}
